// SPHERE WORLD FROM SPACE - a raw-WebGL paper globe (Joshua 2026-09-07:
// "a planet that spins and then a zoom in"). One full-screen quad; the
// fragment shader ray-casts a unit sphere and samples the equirectangular
// planet texture, so there is no dependency and it runs on a phone.
// Visited regions are stamped as small ink marks (uniform array).

const VS = `attribute vec2 p; varying vec2 v; void main(){ v = p; gl_Position = vec4(p, 0.0, 1.0); }`;
const FS = `
precision highp float;
varying vec2 v;
uniform sampler2D tex, texFar;
uniform float aspect, dist, lon, lat, fade, detail;
uniform vec2 marks[24];
uniform int nMarks;
const float PI = 3.14159265;
mat3 rotY(float a){ float c=cos(a), s=sin(a); return mat3(c,0.,-s, 0.,1.,0., s,0.,c); }
mat3 rotX(float a){ float c=cos(a), s=sin(a); return mat3(1.,0.,0., 0.,c,s, 0.,-s,c); }
void main(){
  // camera on +z looking at the origin, sphere radius 1
  vec3 ro = vec3(0.0, 0.0, dist);
  vec3 rd = normalize(vec3(v.x * aspect * 0.55, v.y * 0.55, -1.0));
  float b = dot(ro, rd);
  float c = dot(ro, ro) - 1.0;
  float h = b*b - c;
  // paper night sky: dark ground with a faint warm vignette
  vec3 sky = mix(vec3(0.10,0.08,0.06), vec3(0.16,0.13,0.10), 1.0 - length(v) * 0.6);
  if (h < 0.0) { gl_FragColor = vec4(sky * fade, 1.0); return; }
  float t = -b - sqrt(h);
  vec3 pos = ro + rd * t;
  vec3 n = pos;   // unit sphere
  // spin the globe under the camera: lon about Y, then tilt about X
  vec3 q = rotY(lon) * rotX(lat) * n;
  float u = 0.5 + atan(q.x, q.z) / (2.0 * PI);
  float w = 0.5 - asin(clamp(q.y, -1.0, 1.0)) / PI;
  // from orbit the Slab is a paper blob; the roads and names resolve on the way down
  vec3 col = mix(texture2D(texFar, vec2(u, w)).rgb, texture2D(tex, vec2(u, w)).rgb, detail);
  // visited marks: little ink dots stamped on the paper
  for (int i = 0; i < 24; i++) {
    if (i >= nMarks) break;
    vec2 d = vec2((u - marks[i].x) * 2.0, (w - marks[i].y));
    float r = length(d);
    if (r < 0.0026) col = mix(col, vec3(0.55, 0.12, 0.08), 0.9);
    else if (r < 0.0036) col = mix(col, vec3(0.98, 0.92, 0.80), 0.8);
  }
  // soft paper lighting: a broad light from the upper left, a rim of shadow
  float l = 0.72 + 0.28 * max(0.0, dot(n, normalize(vec3(-0.5, 0.6, 0.8))));
  float rim = smoothstep(0.0, 0.35, -dot(n, rd));
  col *= l * mix(0.55, 1.0, rim);
  // cut-out edge: a cream outline like a paper disc
  float edge = smoothstep(0.0, 0.06, -dot(n, rd));
  col = mix(vec3(0.95, 0.90, 0.78), col, edge);
  gl_FragColor = vec4(col * fade, 1.0);
}`;

export type GlobeMark = [number, number];   // texture uv

export class Globe {
  private gl: WebGLRenderingContext | null = null;
  private prog: WebGLProgram | null = null;
  private tex: WebGLTexture | null = null;
  private texFar: WebGLTexture | null = null;
  private u: Record<string, WebGLUniformLocation | null> = {};
  ready = false;

  constructor(private cv: HTMLCanvasElement) {
    const gl = cv.getContext("webgl", { antialias: true, alpha: false, premultipliedAlpha: false, preserveDrawingBuffer: true });
    if (!gl) return;
    this.gl = gl;
    const mk = (type: number, src: string) => { const s = gl.createShader(type)!; gl.shaderSource(s, src); gl.compileShader(s); return s; };
    const p = gl.createProgram()!;
    gl.attachShader(p, mk(gl.VERTEX_SHADER, VS));
    gl.attachShader(p, mk(gl.FRAGMENT_SHADER, FS));
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) return;
    this.prog = p;
    gl.useProgram(p);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(p, "p");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    for (const n of ["tex", "texFar", "aspect", "dist", "lon", "lat", "fade", "detail", "nMarks"]) this.u[n] = gl.getUniformLocation(p, n);
    for (let i = 0; i < 24; i++) this.u[`marks[${i}]`] = gl.getUniformLocation(p, `marks[${i}]`);
  }

  private upload(img: HTMLImageElement): WebGLTexture | null {
    const gl = this.gl; if (!gl) return null;
    const t = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 0);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, img);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    return t;
  }

  setTexture(img: HTMLImageElement, far?: HTMLImageElement | null) {
    const gl = this.gl; if (!gl || !this.prog) return;
    this.texFar = far ? this.upload(far) : null;
    this.tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 0);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, img);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    this.ready = true;
  }

  /** lon/lat in radians (the point of the planet facing the camera), dist = camera distance (1.15..4), fade 0..1 */
  draw(w: number, h: number, lon: number, lat: number, dist: number, fade: number, marks: GlobeMark[], detail = 1) {
    const gl = this.gl; if (!gl || !this.prog || !this.ready) return;
    if (this.cv.width !== w || this.cv.height !== h) { this.cv.width = w; this.cv.height = h; }
    gl.viewport(0, 0, w, h);
    gl.useProgram(this.prog);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.uniform1i(this.u.tex, 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.texFar || this.tex);
    gl.uniform1i(this.u.texFar, 1);
    gl.uniform1f(this.u.detail, this.texFar ? detail : 1);
    gl.uniform1f(this.u.aspect, w / h);
    gl.uniform1f(this.u.dist, dist);
    gl.uniform1f(this.u.lon, lon);
    gl.uniform1f(this.u.lat, lat);
    gl.uniform1f(this.u.fade, fade);
    const n = Math.min(24, marks.length);
    gl.uniform1i(this.u.nMarks, n);
    for (let i = 0; i < n; i++) gl.uniform2f(this.u[`marks[${i}]`], marks[i][0], marks[i][1]);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  dispose() { const gl = this.gl; if (gl && this.tex) gl.deleteTexture(this.tex); if (gl && this.texFar) gl.deleteTexture(this.texFar); }
}
